import { ReferenceField } from "@/components/admin";

import type { RaRecord } from "ra-core";
import { useTranslate, useRecordContext } from "ra-core";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { RelativeDate } from "../misc/RelativeDate";
import { SaleName } from "../sales/SaleName";
import type { ActivityDealNoteCreated } from "../types";
import { useActivityLogContext } from "./ActivityLogContext";
import { ActivityLogNote } from "./ActivityLogNote";

// Componente para formatar o nome da empresa removendo "em" do início
function CompanyNameFormatter() {
  const record = useRecordContext<{ name: string }>();
  const companyName = record?.name || "";
  
  // Remove "em" do início do nome da empresa se existir
  const formattedName = companyName.startsWith("em") 
    ? companyName.substring(2).trim()
    : companyName;
  
  return <span>{formattedName}</span>;
}

// Componente para formatar o nome do negócio removendo "em" do nome da empresa
function DealNameFormatter() {
  const record = useRecordContext<{ name: string }>();
  const dealName = record?.name || "";
  
  // Procura por padrões como "emNomeDaEmpresa" e substitui por "em NomeDaEmpresa"
  const formattedName = dealName.replace(/\bem([A-Z][a-záàâãéèêíìîóòôõúùûç]+)/g, 'em $1');
  
  return <span>{formattedName}</span>;
}

type ActivityLogDealNoteCreatedProps = {
  activity: RaRecord & ActivityDealNoteCreated;
};

export function ActivityLogDealNoteCreated({
  activity,
}: ActivityLogDealNoteCreatedProps) {
  const context = useActivityLogContext();
  const translate = useTranslate();
  const { dealNote } = activity;
  return (
    <ActivityLogNote
      header={
        <div className="flex flex-row items-center gap-2 flex-grow">
          <ReferenceField
            source="deal_id"
            reference="deals"
            record={dealNote}
            link={false}
          >
            <ReferenceField
              source="company_id"
              reference="companies"
              link={false}
            >
              <CompanyAvatar width={20} height={20} />
            </ReferenceField>
          </ReferenceField>

          <span className="text-sm text-muted-foreground activity-log-container">
            <span className="activity-log-text">
              <ReferenceField
                source="sales_id"
                reference="sales"
                record={activity}
                link={false}
              >
                <SaleName />
              </ReferenceField>
              &nbsp;{translate("simplecrm.dashboard.activity.added_note_about_deal")}&nbsp;
              <ReferenceField
                source="deal_id"
                reference="deals"
                record={dealNote}
                link="show"
              >
                <DealNameFormatter />
              </ReferenceField>
              {context !== "company" && (
                <>
                  {" em "}
                  <ReferenceField
                    source="deal_id"
                    reference="deals"
                    record={dealNote}
                    link={false}
                  >
                    <ReferenceField
                      source="company_id"
                      reference="companies"
                      link="show"
                    >
                      <CompanyNameFormatter />
                    </ReferenceField>
                  </ReferenceField>
                </>
              )}
            </span>
          </span>

          {context === "company" && (
            <span className="text-muted-foreground text-sm">
              <RelativeDate date={activity.date} />
            </span>
          )}
        </div>
      }
      text={dealNote.text}
    />
  );
}
